import { AuthServiceSuccessResponse } from "@screencloud/auth-sdk";
import { useEffect, useState } from "react";
import { appConfig } from "src/appConfig";
import { mapRegionToPlayerSdk } from "src/helpers/playerSdkHelper";
import { useAppContext } from "src/hooks/useAppContext";

/** Seconds the resend action stays in its "sent" state before resetting. */
const RESEND_COOLDOWN_SECONDS = 30;

interface UseEmailVerificationOptions {
  /**
   * Called once the entered code verifies successfully. The wizard uses this to
   * re-sync auth claims in place (refreshAuthToken + refetchUserStatus) so the
   * Payment & Shipping form renders without a full page reload.
   */
  onVerified: () => void | Promise<void>;
}

/**
 * Owns the email-verification code entry for the Pixi wizard's Payment &
 * Shipping gate: auto-submits the 6-digit code, tracks the submitting/error
 * states, and re-requests a code on demand. Verification logic mirrors the
 * standalone {@link VerifyEmailModal} but succeeds into {@link onVerified}
 * (in-place claims re-sync) instead of reloading the page.
 */
export const useEmailVerification = ({
  onVerified,
}: UseEmailVerificationOptions) => {
  const context = useAppContext();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasVerifyCodeError, setHasVerifyCodeError] = useState(false);
  const [hasSendCodeError, setHasSendCodeError] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const userEmail = context.currentUser?.email;

  const onVerifySubmit = async () => {
    if (code.length !== 6) {
      return;
    }
    setSubmitting(true);
    setHasVerifyCodeError(false);
    try {
      const response = await context.sessions.verifyRegistrationCode(code);
      if ((response as AuthServiceSuccessResponse).success) {
        // Leave `submitting` true: a successful re-sync flips isActiveUser and
        // unmounts this view, so the spinner shows through the transition.
        await onVerified();
      } else {
        setHasVerifyCodeError(true);
        setSubmitting(false);
      }
    } catch {
      setHasVerifyCodeError(true);
      setSubmitting(false);
    }
  };

  // Auto-submit once the user has entered all 6 digits. onVerifySubmit reads the
  // latest `code` via closure, so `code` is the intended (and only) trigger.
  useEffect(() => {
    setHasVerifyCodeError(false);
    if (code.length === 6) {
      void onVerifySubmit();
    }
  }, [code]);

  // Tick the resend cooldown down once per second; at zero, drop the "sent"
  // state so the resend action returns. Each tick reschedules the next, which
  // keeps the effect free of stale-closure decrements.
  useEffect(() => {
    if (!codeResent) {
      return;
    }
    if (secondsLeft <= 0) {
      setCodeResent(false);
      setHasSendCodeError(false);
      return;
    }
    const timeout = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timeout);
  }, [codeResent, secondsLeft]);

  const onCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value.toUpperCase().trim());
  };

  const onResendClick = async () => {
    // Start the cooldown synchronously so the click responds instantly rather
    // than sitting dead until the network round-trip resolves.
    setHasSendCodeError(false);
    setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    setCodeResent(true);
    try {
      await fetch(`${appConfig.auth.service}/api/v1/user/create/request-code`, {
        body: JSON.stringify({
          email: userEmail,
          region: mapRegionToPlayerSdk(appConfig.studioPlayerRegion),
        }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
      });
    } catch {
      setHasSendCodeError(true);
    }
  };

  return {
    code,
    userEmail,
    submitting,
    hasVerifyCodeError,
    hasSendCodeError,
    codeResent,
    secondsLeft,
    onCodeChange,
    onResendClick,
  };
};
